import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useAuthStore, SyncStatusState } from '../../store/authStore'; // Assuming SyncStatusState is exported or defined here

// Interface for the raw data structure from Celery via SSE
interface CeleryTaskEvent {
    task_id: string;
    status: string; // e.g., PENDING, STARTED, PROGRESS, SUCCESS, FAILURE, REVOKED, EOS, STREAM_ERROR
    info: any; // Can be a string (for PENDING) or an object for other states
    final_state?: string; // Added for EOS event
    error_detail?: string; // Added for EOS event on error
    message?: string; // Direct message property from backend
    progress?: number; // Direct progress property from backend
}

export const GlobalSyncStatusListener = () => {
    const { syncTaskId, syncTaskType, setSyncTaskId, accessToken, setSyncTaskStatus, syncTaskStatus } = useAuthStore(state => ({
        syncTaskId: state.syncTaskId,
        syncTaskType: state.syncTaskType,
        setSyncTaskId: state.setSyncTaskId,
        accessToken: state.accessToken,
        setSyncTaskStatus: state.setSyncTaskStatus,
        syncTaskStatus: state.syncTaskStatus,
    }));
    const queryClient = useQueryClient();
    const eventSourceRef = useRef<EventSource | null>(null);
    const hasFinishedCallbackCalledRef = useRef<boolean>(false);
    const mainLoadingToastIdRef = useRef<string | null>(null);
    const eosReceivedRef = useRef<boolean>(false); // Flag to track if EOS event was received

    useEffect(() => {
        hasFinishedCallbackCalledRef.current = false;
        eosReceivedRef.current = false; // Reset EOS flag for new task
        if (mainLoadingToastIdRef.current) {
            toast.dismiss(mainLoadingToastIdRef.current);
            mainLoadingToastIdRef.current = null;
        }

        if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
            console.log('[GlobalSyncListener] Previous EventSource closed due to task ID change or cleanup.');
        }

        if (syncTaskId && accessToken) {            console.log(`[GlobalSyncListener] Setting up EventSource for task ID: ${syncTaskId}, task type: ${syncTaskType}`);
            hasFinishedCallbackCalledRef.current = false; // Reset for the new task
            eosReceivedRef.current = false; // Reset EOS flag for new task

            const apiUrl = import.meta.env.VITE_API_BASE_URL || '/api';
            let sseUrl: string;
            if (syncTaskType === 'recommendations') {
                sseUrl = `${apiUrl}/portfolio-command/generate-recommendations/status/${syncTaskId}?token=${encodeURIComponent(accessToken)}`;
            } else {
                sseUrl = `${apiUrl}/trades/sync/status/${syncTaskId}?token=${encodeURIComponent(accessToken)}`;
            }
            console.log(`[GlobalSyncListener] SSE URL: ${sseUrl}`);
            
            // Additional debug for recommendations
            if (syncTaskType === 'recommendations') {
                console.log(`[GlobalSyncListener] [RECOMMENDATIONS DEBUG] About to create EventSource for recommendations with:`);
                console.log(`[GlobalSyncListener] [RECOMMENDATIONS DEBUG] - Task ID: ${syncTaskId}`);
                console.log(`[GlobalSyncListener] [RECOMMENDATIONS DEBUG] - Access Token Length: ${accessToken?.length}`);
                console.log(`[GlobalSyncListener] [RECOMMENDATIONS DEBUG] - Full URL: ${sseUrl}`);
            }

            try {
                const es = new EventSource(sseUrl);
                eventSourceRef.current = es;                es.onopen = () => {
                    console.log(`[GlobalSyncListener] SSE connection opened for task ${syncTaskId}`);
                    if (!mainLoadingToastIdRef.current && !hasFinishedCallbackCalledRef.current) {
                        const taskTypeLabel = syncTaskType === 'recommendations' ? 'Recommendations generation' : 'Trade sync';
                        mainLoadingToastIdRef.current = toast.loading(`${taskTypeLabel}: Connecting to status stream...`, { duration: Infinity });
                        console.log(`[GlobalSyncListener] Initial main loading toast created with ID: ${mainLoadingToastIdRef.current}`);
                        
                        // Log specifically for recommendations debugging
                        if (syncTaskType === 'recommendations') {
                            console.log(`[GlobalSyncListener] [RECOMMENDATIONS DEBUG] Toast created for recommendations with task ID: ${syncTaskId}`);
                        }
                        
                        // Ensure initial status reflects pending connection if not already set by button
                        const currentGlobalStatus = useAuthStore.getState().syncTaskStatus;
                        if (!currentGlobalStatus || currentGlobalStatus.status === 'idle' || (currentGlobalStatus.status === 'pending' && currentGlobalStatus.message !== 'Connecting to status stream...')) {
                            setSyncTaskStatus({
                                status: 'pending',
                                message: 'Connecting to status stream...',
                                progress: 0,
                                details: [...(currentGlobalStatus?.details || []).slice(-10), `${new Date().toISOString()}: SSE connection opened.`]
                            });
                        }
                    }
                };

                es.addEventListener('eos', (event) => {
                    eosReceivedRef.current = true;
                    console.log('[GlobalSyncListener] EOS event received:', event);
                    const eventData = JSON.parse((event as MessageEvent).data) as CeleryTaskEvent;
                    
                    if (mainLoadingToastIdRef.current) {
                        toast.dismiss(mainLoadingToastIdRef.current);
                        mainLoadingToastIdRef.current = null;
                    }

                    const taskTypeLabel = syncTaskType === 'recommendations' ? 'Recommendations Generation' : 'Trade Sync';
                    let finalMessage = eventData.info?.message || "Task processing completed.";
                    
                    // Use hasFinishedCallbackCalledRef to ensure final toast/actions only happen once
                    if (!hasFinishedCallbackCalledRef.current) {
                        hasFinishedCallbackCalledRef.current = true;                        if (eventData.final_state === 'SUCCESS') {
                            toast.success(`${taskTypeLabel} successful: ${finalMessage}`, { duration: 6000 });
                            console.log(`[GlobalSyncListener] [${syncTaskType?.toUpperCase()} DEBUG] SUCCESS toast shown: ${finalMessage}`);
                            setSyncTaskStatus({
                                status: 'completed',
                                message: finalMessage,
                                progress: 100,
                                details: [...(useAuthStore.getState().syncTaskStatus?.details || []).slice(-10), `${new Date().toISOString()}: Task completed successfully (EOS).`]
                            });// Invalidate queries on success
                            if (syncTaskType === 'recommendations') {
                                queryClient.invalidateQueries({ queryKey: ['priority-actions'] });
                                queryClient.invalidateQueries({ queryKey: ['portfolioOverview'] });
                            } else if (syncTaskType === 'trades') {
                                queryClient.invalidateQueries({ queryKey: ['trades'] });
                                queryClient.invalidateQueries({ queryKey: ['pnlSummary'] });
                                queryClient.invalidateQueries({ queryKey: ['accountPnlDaily'] });
                            }
                            queryClient.invalidateQueries({ queryKey: ['userSetupStatus'] });                        } else if (eventData.final_state === 'FAILURE' || eventData.final_state === 'REVOKED' || eventData.final_state === 'STREAM_ERROR') {
                            finalMessage = eventData.info?.message || eventData.error_detail || "Task failed or was revoked.";
                            toast.error(`${taskTypeLabel} failed: ${finalMessage}`, { duration: 8000 });
                            console.log(`[GlobalSyncListener] [${syncTaskType?.toUpperCase()} DEBUG] ERROR toast shown: ${finalMessage}`);
                            setSyncTaskStatus({
                                status: 'error',
                                message: finalMessage,
                                progress: 100,
                                details: [...(useAuthStore.getState().syncTaskStatus?.details || []).slice(-10), `${new Date().toISOString()}: Task failed or ended with error (EOS): ${eventData.final_state}`]
                            });
                        } else if (eventData.final_state === 'CANCELLED_BY_CLIENT') {
                            // This case might be redundant if onerror handles client disconnects, but good for explicit server-side EOS on cancel
                             toast(`${taskTypeLabel} was cancelled by client.`, { duration: 5000 });
                             setSyncTaskStatus({
                                status: 'error', // Or a custom 'cancelled' status
                                message: 'Task cancelled by client (EOS).',
                                progress: 100,
                                details: [...(useAuthStore.getState().syncTaskStatus?.details || []).slice(-10), `${new Date().toISOString()}: Task cancelled by client (EOS).`]
                            });
                        } else {
                             // Generic completion if final_state is not explicitly handled but EOS is received
                            toast(`${taskTypeLabel} finished: ${finalMessage}`, { duration: 5000 });
                             setSyncTaskStatus({
                                status: 'completed', // Or 'idle'
                                message: `Task finished with state: ${eventData.final_state || 'Unknown'} (EOS).`,
                                progress: 100,
                                details: [...(useAuthStore.getState().syncTaskStatus?.details || []).slice(-10), `${new Date().toISOString()}: Task finished (EOS). State: ${eventData.final_state}`]
                            });
                        }
                    }

                    if (eventSourceRef.current) {
                        console.log(`[GlobalSyncListener] Closing EventSource explicitly after EOS for task ${syncTaskId}.`);
                        eventSourceRef.current.close();
                        eventSourceRef.current = null;
                    }
                    setSyncTaskId(null); // Clear task ID after EOS processing
                });

                es.onmessage = (event) => {
                    try {
                        const dataFromServer = JSON.parse(event.data) as CeleryTaskEvent;
                        console.log(`[GlobalSyncListener] SSE message received (parsed):`, dataFromServer);

                        const celeryStatus = dataFromServer.status.toUpperCase();
                        let message = "Processing...";
                        let progress = syncTaskStatus?.progress || 0;
                        
                        // Handle STREAM_ERROR as a terminal message event if it comes through here
                        if (celeryStatus === 'STREAM_ERROR') {
                            if (!hasFinishedCallbackCalledRef.current) {
                                hasFinishedCallbackCalledRef.current = true;
                                const taskTypeLabel = syncTaskType === 'recommendations' ? 'Recommendations Generation' : 'Trade Sync';
                                message = dataFromServer.info?.error || "A stream processing error occurred.";
                                toast.error(`${taskTypeLabel} stream error: ${message}`, { duration: 8000, id: mainLoadingToastIdRef.current || undefined });
                                mainLoadingToastIdRef.current = null;
                                
                                setSyncTaskStatus({
                                    status: 'error',
                                    message: message,
                                    progress: 100,
                                    details: [...(useAuthStore.getState().syncTaskStatus?.details || []).slice(-10), `${new Date().toISOString()}: Stream error: ${message}`]
                                });
                                // Don't close EventSource here, let EOS or onerror handle it
                                // setSyncTaskId(null); // Let EOS or onerror handle this
                            }
                            return; // Stop further processing for this message
                        }                        // Extract message and progress from the SSE data
                        if (typeof dataFromServer.info === 'object' && dataFromServer.info !== null) {
                            message = dataFromServer.info.message || message;
                            progress = dataFromServer.info.progress !== undefined ? dataFromServer.info.progress : (dataFromServer.info.current && dataFromServer.info.total ? (dataFromServer.info.current / dataFromServer.info.total) * 100 : progress);
                        } else if (typeof dataFromServer.info === 'string') {
                            message = dataFromServer.info;
                        }
                        
                        // Also check for progress and message directly on the dataFromServer object (backend may send it this way)
                        if (dataFromServer.message !== undefined) {
                            message = dataFromServer.message;
                        }
                        if (dataFromServer.progress !== undefined) {
                            progress = dataFromServer.progress;
                        }
                          if (celeryStatus === 'SUCCESS' || celeryStatus === 'COMPLETED' || celeryStatus === 'FAILURE' || celeryStatus === 'REVOKED') {
                            progress = 100;
                        }


                        const detailsUpdate = `${new Date().toISOString()}: ${message} (Status: ${celeryStatus})`;                        const newGlobalStatus: SyncStatusState = {
                            status: celeryStatus === 'SUCCESS' || celeryStatus === 'COMPLETED' ? 'completed' : (celeryStatus === 'FAILURE' || celeryStatus === 'REVOKED' ? 'error' : (celeryStatus === 'PROGRESS' || celeryStatus === 'STARTED' || celeryStatus === 'RUNNING' ? 'running' : 'pending')),
                            message: message,
                            progress: Math.min(100, Math.max(0, Math.round(progress))), // Ensure progress is 0-100
                            details: [...(useAuthStore.getState().syncTaskStatus?.details || []).slice(-10), detailsUpdate]
                        };
                        setSyncTaskStatus(newGlobalStatus);

                        const taskTypeLabel = syncTaskType === 'recommendations' ? 'Recommendations Generation' : 'Trade Sync';                        if (celeryStatus === 'SUCCESS' || celeryStatus === 'COMPLETED') {
                            if (!hasFinishedCallbackCalledRef.current) {
                                hasFinishedCallbackCalledRef.current = true; // Mark as finished to prevent onerror from triggering error toasts
                                
                                // Don't create success toast here - let EOS handler do it to avoid duplicates
                                // Just dismiss the loading toast and update status
                                if (mainLoadingToastIdRef.current) {
                                    toast.dismiss(mainLoadingToastIdRef.current);
                                    mainLoadingToastIdRef.current = null;
                                }

                                // Show success toast since we're not waiting for EOS
                                toast.success(`${taskTypeLabel} completed successfully!`, { duration: 5000 });

                                // Invalidate queries, but don't close/clear task yet.
                                if (syncTaskType === 'recommendations') {
                                    queryClient.invalidateQueries({ queryKey: ['priority-actions'] });
                                    queryClient.invalidateQueries({ queryKey: ['portfolioOverview'] });
                                } else if (syncTaskType === 'trades') {
                                    queryClient.invalidateQueries({ queryKey: ['trades'] });
                                    queryClient.invalidateQueries({ queryKey: ['pnlSummary'] });
                                    queryClient.invalidateQueries({ queryKey: ['accountPnlDaily'] });
                                }
                                queryClient.invalidateQueries({ queryKey: ['userSetupStatus'] });

                                // DO NOT close EventSource or setSyncTaskId(null) here. Wait for EOS.
                            }
                        } else if (celeryStatus === 'FAILURE' || celeryStatus === 'REVOKED') {
                            if (!hasFinishedCallbackCalledRef.current) {
                                // Don't create error toast here - let EOS handler do it to avoid duplicates
                                // Just dismiss the loading toast
                                if (mainLoadingToastIdRef.current) {
                                    toast.dismiss(mainLoadingToastIdRef.current);
                                    mainLoadingToastIdRef.current = null;
                                }
                                
                                // DO NOT close EventSource or setSyncTaskId(null) here. Wait for EOS.
                            }
                        } else { // PENDING, STARTED, PROGRESS
                            if (mainLoadingToastIdRef.current) {
                                toast.loading(`${taskTypeLabel}: ${message} (${newGlobalStatus.progress}%)`, { id: mainLoadingToastIdRef.current, duration: Infinity });
                            } else if (!hasFinishedCallbackCalledRef.current) { // Avoid creating new toast if already finished
                                mainLoadingToastIdRef.current = toast.loading(`${taskTypeLabel}: ${message} (${newGlobalStatus.progress}%)`, { duration: Infinity });
                            }
                        }

                    } catch (error) {
                        console.error('[GlobalSyncListener] Error parsing SSE message or processing update:', error);
                        if (!hasFinishedCallbackCalledRef.current) {
                             // This is an error in client-side processing of a valid message, not a connection error.
                            toast.error('Error processing status update from server.', { duration: 5000 });
                            // Optionally, treat as a task failure if critical for data integrity
                            // hasFinishedCallbackCalledRef.current = true;
                            // setSyncTaskStatus({ status: 'error', message: 'Client error processing server update.', progress: 100, details: [] });
                            // if (eventSourceRef.current) eventSourceRef.current.close();
                            // setSyncTaskId(null);
                        }
                    }
                };                es.onerror = (error) => {
                    console.error(`[GlobalSyncListener] SSE error for task ${syncTaskId}:`, error);
                    
                    // Additional debug for recommendations
                    if (syncTaskType === 'recommendations') {
                        console.log(`[GlobalSyncListener] [RECOMMENDATIONS DEBUG] SSE error occurred for recommendations task ${syncTaskId}`);
                        console.log(`[GlobalSyncListener] [RECOMMENDATIONS DEBUG] Error object:`, error);
                        console.log(`[GlobalSyncListener] [RECOMMENDATIONS DEBUG] EventSource readyState:`, es.readyState);
                    }

                    if (eosReceivedRef.current) {
                        console.log(`[GlobalSyncListener] SSE error for task ${syncTaskId}, but EOS event was already received. Assuming clean closure.`);
                        if (eventSourceRef.current) {
                            eventSourceRef.current.close();
                            eventSourceRef.current = null;
                        }
                        // If EOS was received, the task ID should have been cleared by the EOS handler.
                        // If not, ensure it's cleared now.
                        if (useAuthStore.getState().syncTaskId === syncTaskId) {
                            setSyncTaskId(null);
                        }
                        return;
                    }

                    if (hasFinishedCallbackCalledRef.current) {
                        console.log(`[GlobalSyncListener] SSE error for task ${syncTaskId}, but task already handled. Likely server closing stream post-completion or error already processed.`);
                        if (eventSourceRef.current) {
                            eventSourceRef.current.close();
                            eventSourceRef.current = null;
                        }
                        return;
                    }

                    hasFinishedCallbackCalledRef.current = true; // Mark as handled to prevent re-entry

                    if (mainLoadingToastIdRef.current) {
                        toast.dismiss(mainLoadingToastIdRef.current);
                        mainLoadingToastIdRef.current = null;
                    }

                    const taskTypeLabel = syncTaskType === 'recommendations' ? 'Recommendations generation' : 'Trade sync';
                    toast.error(`${taskTypeLabel} connection error or task unexpectedly ended.`, { duration: 8000 });

                    setSyncTaskStatus({
                        status: 'error',
                        message: 'Connection error with the server or the task ended unexpectedly.',
                        progress: 100, // Or an appropriate progress value for an error
                        details: [...(useAuthStore.getState().syncTaskStatus?.details || []).slice(-10), `${new Date().toISOString()}: SSE connection error or unexpected end (onerror).`]
                    });

                    if (eventSourceRef.current) {
                        eventSourceRef.current.close();
                        eventSourceRef.current = null;
                    }
                    setSyncTaskId(null);
                    queryClient.invalidateQueries(); // General invalidation as state is uncertain
                };

            } catch (error) {
                 console.error(`[GlobalSyncListener] Failed to create EventSource for task ${syncTaskId}:`, error);
                 if (!hasFinishedCallbackCalledRef.current) {
                     hasFinishedCallbackCalledRef.current = true;
                     if (mainLoadingToastIdRef.current) {
                        toast.dismiss(mainLoadingToastIdRef.current);
                        mainLoadingToastIdRef.current = null;
                     }
                     const taskTypeLabel = syncTaskType === 'recommendations' ? 'Recommendations generation' : 'Trade sync';
                     toast.error(`Failed to connect to ${taskTypeLabel} status stream.`, { duration: 8000 });
                     setSyncTaskStatus({
                         status: 'error',
                         message: 'Failed to establish connection for status updates.',
                         progress: 0,
                         details: [...(useAuthStore.getState().syncTaskStatus?.details || []).slice(-10), `${new Date().toISOString()}: Failed to create EventSource.`]
                     });
                     setSyncTaskId(null);
                 }
            }

        } else if (syncTaskId && !accessToken) {
            console.warn(`[GlobalSyncListener] SSE connection skipped for task ${syncTaskId}: No access token.`);
            if (!hasFinishedCallbackCalledRef.current) {
                hasFinishedCallbackCalledRef.current = true;
                setSyncTaskStatus({ status: 'error', message: 'Authentication token missing, cannot monitor task.', progress: 0, details: [] });
                setSyncTaskId(null); // Clear task as it cannot be monitored
            }
        }

        return () => {
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
                eventSourceRef.current = null;
                console.log(`[GlobalSyncListener] EventSource cleanup for task ID: ${syncTaskId}`);
            }
            if (mainLoadingToastIdRef.current) {
                toast.dismiss(mainLoadingToastIdRef.current);
                mainLoadingToastIdRef.current = null;
                console.log(`[GlobalSyncListener] Dismissing main loading toast (${mainLoadingToastIdRef.current}) during cleanup.`);
            }
             // If the component is unmounting and a task was active but not "finished" by onmessage/onerror logic,
             // it's good practice to ensure the task ID is cleared to prevent orphaned states.
             // However, this might be too aggressive if unmount is temporary (e.g. HMR)
             // const currentTaskId = useAuthStore.getState().syncTaskId;
             // if (currentTaskId && !hasFinishedCallbackCalledRef.current) {
             //    console.log(`[GlobalSyncListener] Clearing unfinished task ${currentTaskId} on unmount/cleanup.`);
             //    setSyncTaskId(null);
             //    setSyncTaskStatus({ status: 'idle', message: 'Listener cleaned up.', progress: 0, details: [] });
             // }
        };
    }, [syncTaskId, syncTaskType, accessToken, setSyncTaskId, queryClient, setSyncTaskStatus]); // Removed syncTaskStatus from deps to avoid loops if it updates frequently

    return null;
};